import { useMemo } from "react"
import { Tag } from "@/schema"
import { ComboboxInput } from "@ui/forms/inputs/combobox-input"
import { cn } from "@ui/utils"
import { Check, X } from "lucide-react"

import { useFetchTags } from "@/hooks/api/tags"

interface EntityLabelSelectorProps {
  entityType: "task" | "opportunity" | "note"
  id?: string
  label?: Tag | null
  labelId?: string | null
  onLabelChange: (label: Tag | null) => void
  triggerButton?: React.ReactNode
  disabled?: boolean
  contentProps?: React.ComponentPropsWithoutRef<typeof ComboboxInput>["contentProps"]
  className?: string
  error?: boolean
}

const CLEAR_VALUE = "__clear__"

const sortTags = (tags: Tag[]) => [...tags].sort((a, b) => a.name.localeCompare(b.name))

export function EntityLabelSelector({
  entityType,
  id = `${entityType}-label-selector`,
  label,
  labelId,
  onLabelChange,
  triggerButton,
  disabled = false,
  contentProps,
  className,
  error,
}: EntityLabelSelectorProps) {
  const { data: tagsData } = useFetchTags(entityType)

  const availableTags = useMemo(() => {
    if (!tagsData?.tags) return []
    return sortTags(tagsData.tags)
  }, [tagsData?.tags])

  const selectedLabel = label ?? availableTags.find((tag) => tag.id === labelId) ?? null

  const options = useMemo(() => {
    const tagOptions = availableTags.map((tag) => ({
      value: tag.id,
      label: tag.name,
      icon: (
        <div
          className="size-1.5 flex-none rounded-full"
          style={{ backgroundColor: tag.color || "var(--color-secondary)" }}
        />
      ),
    }))

    if (selectedLabel) {
      return [
        {
          value: CLEAR_VALUE,
          label: "Clear label",
          icon: <X className="text-muted-foreground h-4 w-4" />,
        },
        ...tagOptions,
      ]
    }

    return tagOptions
  }, [availableTags, selectedLabel])

  const handleChange = (value: string) => {
    if (value === CLEAR_VALUE) {
      onLabelChange(null)
    } else {
      const selectedTag = availableTags.find((tag) => tag.id === value)
      if (selectedTag) {
        onLabelChange(selectedTag)
      }
    }
  }

  return (
    <ComboboxInput
      id={id}
      value={selectedLabel?.id}
      onChange={handleChange}
      options={options}
      placeholder="Select label"
      emptyText="No labels found"
      triggerButton={triggerButton}
      disabled={disabled}
      error={error}
      contentProps={contentProps}
      className={className}
      renderOption={(option) => (
        <>
          {option.icon}
          <span>{option.label}</span>
          {option.value !== CLEAR_VALUE && (
            <Check
              className={cn("ml-auto h-4 w-4", selectedLabel?.id === option.value ? "opacity-100" : "opacity-0")}
            />
          )}
        </>
      )}
    />
  )
}
